import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AcademicCapIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningMode } from '../../contexts/LearningModeContext';
import { findHelpModule } from '../../help/modules';
import { canSeeHelp } from '../../help/audience';
import HelpModuleBody from './HelpModuleBody';

/**
 * Tanuló módban az aktuális modul útmutatója az oldal tetején. Az útvonalból
 * találja meg a modult, így az oldalaknak nem kell tudniuk róla.
 */
const ModuleGuide: React.FC = () => {
  const { user } = useAuth();
  const { enabled, setEnabled, isCollapsed, setCollapsed } = useLearningMode();
  const location = useLocation();

  const module = findHelpModule(location.pathname);
  if (!enabled || !module || !canSeeHelp(user, module)) return null;

  const collapsed = isCollapsed(module.id);

  return (
    <section
      aria-label={`Útmutató: ${module.title}`}
      className="mb-6 rounded-xl border border-primary-200 bg-primary-50/70 shadow-sm"
    >
      <button
        type="button"
        onClick={() => setCollapsed(module.id, !collapsed)}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <AcademicCapIcon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-primary-700">Tanuló mód</span>
          <span className="block text-sm font-semibold text-gray-900">{module.title} - így működik</span>
        </span>
        {collapsed ? (
          <ChevronDownIcon className="h-5 w-5 text-primary-600" />
        ) : (
          <ChevronUpIcon className="h-5 w-5 text-primary-600" />
        )}
      </button>

      {!collapsed && (
        <div className="border-t border-primary-100 px-4 pb-4 pt-3">
          <HelpModuleBody module={module} />
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <Link to={`/help#${module.id}`} className="font-medium text-primary-700 hover:text-primary-800">
              Összes funkció a Súgóban →
            </Link>
            <button
              type="button"
              onClick={() => setEnabled(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Tanuló mód kikapcsolása
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ModuleGuide;
